import React, { useState, memo } from 'react';
import { Check, ArrowRight, Brain, BookOpen, Puzzle, ShieldCheck, Download, Infinity, Mail, Printer } from 'lucide-react';
import { BonusCard } from './components/BonusCard';
import { PurchaseNotification } from './components/PurchaseNotification';
import { InlineTimer } from './components/InlineTimer';
const CHECKOUT_URL = 'https://checkout.ebookditales.com/cart/42753407844415:1';
const MOCKUP_IMAGE = 'https://cdn.magicpatterns.com/uploads/gP6QuGfmJNLScn8bussM7X/nuevo_mock_up_psicopedagogia-removebg-preview.png';
interface PlanOption {
  id: string;
  emoji: string;
  title: string;
  description: string;
  oldPrice: string;
  price: string;
  checkoutUrl: string;
  highlighted?: boolean;
  badge?: string;
  includes: string[];
}
const PLAN_OPTIONS: PlanOption[] = [{
  id: 'base',
  emoji: '📦',
  title: 'MEGAPACK BASE',
  description: 'Incluye TDAH + Dislexia · Material completo + 5 bonos profesionales',
  oldPrice: '$79.000',
  price: '$19.990',
  checkoutUrl: 'https://checkout.ebookditales.com/cart/42753407844415:1',
  includes: ['+100 páginas de material clínico', 'Sistemas para TDAH, Dislexia y TEA', '5 bonos profesionales incluidos', 'Acceso inmediato y de por vida']
}, {
  id: 'antidispersion',
  emoji: '🧠',
  title: 'MEGAPACK + ANTIDISPERSIÓN',
  description: 'Incluye TDAH + Dislexia · Todo el MegaPack + 20 fichas para cuando el niño pierde el foco en sesión',
  oldPrice: '$83.990',
  price: '$24.980',
  checkoutUrl: 'https://checkout.ebookditales.com/cart/42924742344767:1',
  includes: ['Todo lo del MegaPack Base', '20 fichas de recuperación de atención', 'Para cuando el niño pierde el foco en sesión', 'Intervenís sin improvisar y recuperás la atención']
}, {
  id: 'crisis',
  emoji: '🚨',
  title: 'MEGAPACK + CRISIS EN SESIÓN',
  description: 'Incluye TDAH + Dislexia · Todo el MegaPack + 20 fichas para cuando el niño entra en llanto, enojo o bloqueo',
  oldPrice: '$83.990',
  price: '$24.980',
  checkoutUrl: 'https://checkout.ebookditales.com/cart/42924753289279:1',
  includes: ['Todo lo del MegaPack Base', '20 fichas paso a paso para crisis en sesión', 'Para cuando el niño entra en llanto, enojo o bloqueo', 'Intervenís con seguridad y contención']
}, {
  id: 'completo',
  emoji: '⚡',
  title: 'MEGAPACK COMPLETO',
  description: 'Incluye TDAH + Dislexia · Todo el MegaPack + Antidispersión + Crisis en Sesión',
  oldPrice: '$88.990',
  price: '$28.990',
  checkoutUrl: 'https://checkout.ebookditales.com/cart/42924756140095:1',
  highlighted: true,
  badge: 'MÁS ELEGIDO',
  includes: ['Todo lo del MegaPack Base', 'Sistema Antidispersión™ (20 fichas)', 'Protocolo Crisis en Sesión™ (20 fichas)', '40 fichas extra para sostener cualquier sesión']
}];
export function App() {
  const [selectedPlan, setSelectedPlan] = useState('completo');
  const currentPlan = PLAN_OPTIONS.find(p => p.id === selectedPlan)!;
  return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-0">
      <PurchaseNotification data-id="element-1" />

      {/* 1. HERO SECTION */}
      <header className="bg-gradient-to-b from-pastel-50 to-white pt-6 pb-6 px-4 md:pt-16 md:pb-16 overflow-hidden" data-id="element-2">
        <div className="max-w-5xl mx-auto text-center" data-id="element-3">
          <div className="inline-block bg-pastel-100 text-pastel-800 px-3 py-1 md:px-4 md:py-1.5 rounded-full text-[10px] md:text-sm font-bold tracking-wide mb-3 md:mb-6" data-id="element-4">
            PARA PSICOPEDAGOGAS QUE ATIENDEN NIÑOS TODAS LAS SEMANAS
          </div>

          <h1 className="text-[22px] md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-2 md:mb-6 max-w-4xl mx-auto" data-id="element-5">
            DEJÁ DE TERMINAR EL DÍA AGOTADA PENSANDO QUÉ HACER EN LA PRÓXIMA
            SESIÓN
          </h1>

          <p className="text-sm md:text-2xl text-gray-600 font-medium mb-1 md:mb-4 max-w-3xl mx-auto" data-id="element-6">
            El sistema psicopedagógico que te da 1 año de material listo para
            usar
          </p>

          <h2 className="text-base md:text-3xl font-bold text-pastel-700 mb-3 md:mb-8" data-id="element-7">
            MegaPack Psicopedagógico Profesional
          </h2>

          <div className="relative max-w-[180px] md:max-w-2xl mx-auto mb-3 md:mb-8" data-id="element-8">
            <a href={CHECKOUT_URL} className="block group" data-id="element-9">
              <img src={MOCKUP_IMAGE} alt="MegaPack Psicopedagógico Profesional" className="w-full h-auto drop-shadow-2xl group-hover:scale-105 transition-transform duration-500" data-id="element-10" />
            </a>
          </div>

          <div className="flex flex-col items-center gap-1 md:gap-2 mb-2 md:mb-6 text-xs md:text-base font-medium text-gray-700" data-id="element-11">
            <span className="flex items-center gap-1.5 md:gap-2" data-id="element-12">
              <Check className="w-4 h-4 md:w-5 md:h-5 text-emerald-500" data-id="element-13" />
              24 sesiones estructuradas
            </span>
            <span className="flex items-center gap-1.5 md:gap-2" data-id="element-14">
              <Check className="w-4 h-4 md:w-5 md:h-5 text-emerald-500" data-id="element-15" />
              Sistemas completos para TDAH, Dislexia y TEA
            </span>
            <span className="flex items-center gap-1.5 md:gap-2" data-id="element-16">
              <Check className="w-4 h-4 md:w-5 md:h-5 text-emerald-500" data-id="element-17" />
              Más de 100 páginas de material clínico
            </span>
          </div>

          <p className="text-sm md:text-xl text-gray-600 font-medium mb-3 md:mb-8" data-id="element-18">
            Descargás. Imprimís. Aplicás.
          </p>

          <div className="max-w-md mx-auto" data-id="element-19">
            <a href={CHECKOUT_URL} className="bg-emerald-500 hover:bg-emerald-600 text-white text-base md:text-xl font-bold py-3 md:py-4 px-6 md:px-8 rounded-xl shadow-lg hover:shadow-emerald-500/30 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 md:gap-3 w-full" data-id="element-20">
              ACCEDER AL MEGAPACK AHORA
              <ArrowRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-21" />
            </a>
            <p className="text-xs md:text-sm text-gray-500 mt-2 md:mt-4 font-medium" data-id="element-22">
              Pago único de $19.990 ARS · Acceso inmediato
            </p>
          </div>
        </div>
      </header>

      {/* 2. PROBLEM SECTION */}
      <section className="py-20 px-4 bg-white" data-id="element-23">
        <div className="max-w-3xl mx-auto" data-id="element-24">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-12 text-center" data-id="element-25">
            SI SOS PSICOPEDAGOGA, ESTO TE VA A SONAR FAMILIAR
          </h2>

          <div className="space-y-6 text-lg md:text-2xl text-gray-700 leading-relaxed text-center font-medium max-w-2xl mx-auto" data-id="element-26">
            <p data-id="element-27">Terminás tu jornada.</p>
            <p data-id="element-28">Pero el trabajo sigue.</p>
            <p data-id="element-29">
              Pensás qué actividad preparar. Qué llevar a la próxima sesión.
              Cómo ayudar a ese niño que no avanza.
            </p>
            <p data-id="element-30">La cabeza no se apaga.</p>
            <p data-id="element-31">No por los niños.</p>
            <p data-id="element-32">Sino porque tenés que sostener todo el sistema sola.</p>
            <p data-id="element-33">Actividades. Planificación. Material. Seguimiento.</p>
            <p className="text-rose-700 font-bold text-xl md:text-3xl pt-4" data-id="element-34">
              Cada semana empezando desde cero.
            </p>
          </div>
        </div>
      </section>

      {/* 3. SOLUTION SECTION */}
      <section className="py-20 px-4 bg-pastel-100 text-center" data-id="element-35">
        <div className="max-w-4xl mx-auto" data-id="element-36">
          <span className="text-amber-600 font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-37">
            UN SISTEMA QUE TE SOSTIENE
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold mb-8 text-gray-900" data-id="element-38">
            MegaPack Psicopedagógico Profesional™
          </h2>

          <div className="space-y-6 text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-39">
            <p className="font-semibold text-gray-800" data-id="element-40">
              Un sistema completo para que no tengas que inventar sesiones cada
              semana.
            </p>
            <p data-id="element-41">Entrás al consultorio con claridad.</p>
            <p data-id="element-42">
              Sabés qué trabajar. Sabés qué material usar. Sabés cómo continuar
              el proceso.
            </p>
            <p className="font-bold text-pastel-700 text-2xl md:text-3xl pt-4" data-id="element-43">
              Solo abrís el material y aplicás.
            </p>
          </div>
        </div>
      </section>

      {/* 4. QUÉ INCLUYE SECTION */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-44">
        <div className="max-w-4xl mx-auto" data-id="element-45">
          <div className="text-center mb-12" data-id="element-46">
            <span className="text-4xl mb-4 block" data-id="element-47">📦</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900" data-id="element-48">
              QUÉ INCLUYE EL MEGAPACK
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-10" data-id="element-49">
            {[{
            text: 'Más de 100 páginas de material clínico listo',
            icon: '📄'
          }, {
            text: 'Actividades de estimulación cognitiva',
            icon: '🧠'
          }, {
            text: 'Material de lectoescritura',
            icon: '✏️'
          }, {
            text: 'Ejercicios para funciones ejecutivas',
            icon: '⚙️'
          }, {
            text: 'Protocolos de evaluación',
            icon: '📋'
          }, {
            text: 'Planillas de seguimiento',
            icon: '📊'
          }, {
            text: 'Reportes para padres',
            icon: '👨‍👩‍👧'
          }].map((item, i) => <div key={i} className="flex items-center gap-4 bg-white rounded-xl p-4 md:p-5 shadow-sm border border-gray-100" data-id="element-50">
                <span className="text-2xl flex-shrink-0" data-id="element-51">{item.icon}</span>
                <span className="text-base md:text-lg font-medium text-gray-800" data-id="element-52">
                  {item.text}
                </span>
              </div>)}
          </div>

          <div className="flex justify-center mb-10" data-id="element-53">
            <a href={CHECKOUT_URL} className="block group" data-id="element-54">
              <img src={MOCKUP_IMAGE} alt="Qué incluye el MegaPack" className="w-[280px] md:w-[400px] drop-shadow-xl group-hover:scale-105 transition-transform duration-500" data-id="element-55" />
            </a>
          </div>
        </div>
      </section>

      {/* 5. SISTEMAS INCLUIDOS SECTION */}
      <section className="py-20 px-4 bg-white" data-id="element-56">
        <div className="max-w-6xl mx-auto" data-id="element-57">
          <div className="text-center mb-16" data-id="element-58">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 flex items-center justify-center gap-3" data-id="element-59">
              <span data-id="element-60">🔥</span> SISTEMAS INCLUIDOS
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-61">
            <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-pastel-100 flex flex-col h-full" data-id="element-62">
              <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-6" data-id="element-63">
                <Brain className="w-8 h-8 text-amber-600" data-id="element-64" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-65">TDAH</h3>
              <ul className="space-y-3 text-gray-600" data-id="element-66">
                <li className="flex items-start gap-2" data-id="element-67">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-68" />
                  <span data-id="element-69">24 sesiones estructuradas</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-70">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-71" />
                  <span data-id="element-72">+120 fichas clínicas</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-73">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-74" />
                  <span data-id="element-75">Ejercicios para atención e inhibición</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-pastel-100 flex flex-col h-full" data-id="element-76">
              <div className="w-14 h-14 bg-pastel-100 rounded-2xl flex items-center justify-center mb-6" data-id="element-77">
                <BookOpen className="w-8 h-8 text-pastel-700" data-id="element-78" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-79">
                DISLEXIA
              </h3>
              <ul className="space-y-3 text-gray-600" data-id="element-80">
                <li className="flex items-start gap-2" data-id="element-81">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-82" />
                  <span data-id="element-83">72 actividades progresivas</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-84">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-85" />
                  <span data-id="element-86">Trabajo en lectura y escritura</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-87">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-88" />
                  <span data-id="element-89">Seguimiento del proceso</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-pastel-100 flex flex-col h-full" data-id="element-90">
              <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mb-6" data-id="element-91">
                <Puzzle className="w-8 h-8 text-teal-600" data-id="element-92" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-93">TEA</h3>
              <p className="text-gray-600 mb-3" data-id="element-94">Ejercicios para trabajar:</p>
              <ul className="space-y-3 text-gray-600" data-id="element-95">
                <li className="flex items-start gap-2" data-id="element-96">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-97" />
                  <span data-id="element-98">Atención conjunta</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-99">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-100" />
                  <span data-id="element-101">Regulación emocional</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-102">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-103" />
                  <span data-id="element-104">Comunicación funcional</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-105">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-106" />
                  <span data-id="element-107">Seguimiento estructurado</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 6. BONOS PROFESIONALES */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-108">
        <div className="max-w-7xl mx-auto" data-id="element-109">
          <div className="text-center mb-16" data-id="element-110">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3" data-id="element-111">
              <span data-id="element-112">🎁</span> BONOS PROFESIONALES
            </h2>
            <p className="text-xl text-gray-600 font-medium" data-id="element-113">Además recibís:</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-id="element-114">
            <BonusCard number={1} title="Guía de Estrategias Terapéuticas" description="Intervenciones prácticas para déficit atencional, lectura y regulación." image="https://cdn.magicpatterns.com/uploads/gMdT3WBzziTMuJSZFjDdfe/c3a2b0e9-7269-42e6-8eb5-fad89736cdde-removebg-preview.png" data-id="element-115" />
            <BonusCard number={2} title="Kit de Reportes para Padres" description="Plantillas profesionales para comunicar avances sin perder horas." image="https://cdn.magicpatterns.com/uploads/6483v4TDdPnNCaaLS7obqY/64501d31-5600-4e64-9b1f-6bef7b5f0096-removebg-preview.png" data-id="element-116" />
            <BonusCard number={3} title="Checklist Profesional" description="Lista completa para organizar cada sesión sin olvidar ningún detalle." image="https://cdn.magicpatterns.com/uploads/2Uv3hUNxq8z8J5ptJtbZRi/44941fd4-0a91-47c6-a4fc-0907a92fb06d-removebg-preview.png" data-id="element-117" />
            <BonusCard number={4} title="Cuaderno de Regulación Emocional" description="Material para trabajar respiración, emociones y autocontrol." image="https://cdn.magicpatterns.com/uploads/6emCNR8cauzW1F9u81jn29/0439c90e-8404-435e-aeb3-dbb53b7bc719-removebg-preview.png" data-id="element-118" />
            <BonusCard number={5} title="Cuaderno Cognitivo Profesional" description="Actividades para atención, memoria y funciones ejecutivas." image="https://cdn.magicpatterns.com/uploads/c6NuN13ZWTy1g7rdtjkda5/df77002f-5ffc-4b70-a20b-c4611865fefb-removebg-preview.png" data-id="element-119" />
          </div>
        </div>
      </section>

      {/* 7. TODO LO QUE RECIBÍS HOY */}
      <section className="py-20 px-4 bg-white" data-id="element-120">
        <div className="max-w-3xl mx-auto" data-id="element-121">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12" data-id="element-122">
            TODO LO QUE RECIBÍS HOY
          </h2>

          <div className="space-y-8" data-id="element-123">
            <div className="bg-pastel-50 rounded-2xl p-6 md:p-8 border border-pastel-200" data-id="element-124">
              <ul className="space-y-4" data-id="element-125">
                {['MegaPack Psicopedagógico Profesional', 'Sistema completo para TDAH', 'Sistema completo para Dislexia', 'Material de apoyo para TEA'].map((item, i) => <li key={i} className="flex items-center gap-3 text-lg text-gray-800 font-medium" data-id="element-126">
                    <Check className="w-6 h-6 text-pastel-700 flex-shrink-0" data-id="element-127" />
                    {item}
                  </li>)}
              </ul>
            </div>

            <div className="bg-amber-50 rounded-2xl p-6 md:p-8 border border-amber-200" data-id="element-128">
              <p className="text-sm font-bold text-amber-700 uppercase tracking-wider mb-4" data-id="element-129">
                BONOS:
              </p>
              <ul className="space-y-4" data-id="element-130">
                {['Guía de Estrategias Terapéuticas', 'Kit de Reportes para Padres', 'Checklist Profesional', 'Cuaderno de Regulación Emocional', 'Cuaderno Cognitivo Profesional'].map((item, i) => <li key={i} className="flex items-center gap-3 text-lg text-gray-800 font-medium" data-id="element-131">
                    <Check className="w-6 h-6 text-amber-500 flex-shrink-0" data-id="element-132" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 8. ESTE MEGAPACK ES PARA VOS SI */}
      <section className="py-20 px-4 bg-pastel-50" data-id="element-133">
        <div className="max-w-3xl mx-auto" data-id="element-134">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12" data-id="element-135">
            ESTE MEGAPACK ES PARA VOS SI
          </h2>

          <div className="grid md:grid-cols-2 gap-6 mb-16" data-id="element-136">
            {['Atendés niños todas las semanas', 'Pasás horas preparando material', 'Querés sesiones más claras y organizadas', 'Querés terminar el día sin seguir trabajando en casa'].map((item, i) => <div key={i} className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm border border-pastel-200" data-id="element-137">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-138">
                  <Check className="w-5 h-5 text-emerald-600" data-id="element-139" />
                </div>
                <span className="text-lg text-gray-800 font-medium" data-id="element-140">
                  {item}
                </span>
              </div>)}
          </div>

          {/* AUTORA */}
          <div className="bg-white rounded-2xl p-6 md:p-10 shadow-md border border-pastel-200 flex flex-col md:flex-row items-center gap-8" data-id="element-141">
            <div className="w-64 h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden flex-shrink-0 border-4 border-pastel-200 shadow-lg" data-id="element-142">
              <img src="https://cdn.magicpatterns.com/uploads/76Ts6t7DHWJboFyXEsAcGo/WhatsApp_Image_2026-03-06_at_4.35.36_PM.jpg" alt="Lic. Mariana Estrecher" className="w-full h-full object-cover" data-id="element-143" />
            </div>
            <div className="text-center md:text-left" data-id="element-144">
              <h3 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-145">
                Lic. Mariana Estrecher
              </h3>
              <p className="text-pastel-700 font-semibold mb-4" data-id="element-146">
                Psicopedagoga
              </p>
              <p className="text-gray-600 leading-relaxed mb-3" data-id="element-147">
                Especialista en intervención en dificultades de aprendizaje.
              </p>
              <p className="text-gray-600 leading-relaxed mb-3" data-id="element-148">
                Junto a otras colegas psicopedagogas desarrolló este sistema de
                materiales profesionales basados en la experiencia real de
                trabajo en consultorio con niños que presentan TDAH, dislexia y
                dificultades en la lectoescritura.
              </p>
              <p className="text-gray-700 leading-relaxed font-medium" data-id="element-149">
                Hoy estas herramientas ayudan a profesionales a organizar mejor
                sus sesiones, trabajar con más seguridad clínica y acompañar el
                progreso de cada niño de forma estructurada.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. ACCESO INMEDIATO */}
      <section className="py-20 px-4 bg-white" data-id="element-150">
        <div className="max-w-3xl mx-auto" data-id="element-151">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4" data-id="element-152">
            ACCESO INMEDIATO
          </h2>
          <p className="text-center text-gray-600 text-lg mb-10" data-id="element-153">
            Después de comprar recibís:
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-154">
            {[{
            icon: <Download className="w-7 h-7 text-teal-600" data-id="element-155" />,
            text: 'Acceso inmediato al material',
            bg: 'bg-teal-50',
            border: 'border-teal-100'
          }, {
            icon: <Mail className="w-7 h-7 text-pastel-600" data-id="element-156" />,
            text: 'Descarga digital en PDF',
            bg: 'bg-pastel-50',
            border: 'border-pastel-200'
          }, {
            icon: <Infinity className="w-7 h-7 text-amber-600" data-id="element-157" />,
            text: 'Acceso de por vida',
            bg: 'bg-amber-50',
            border: 'border-amber-100'
          }, {
            icon: <Printer className="w-7 h-7 text-rose-600" data-id="element-158" />,
            text: 'Podés imprimir cuando lo necesites',
            bg: 'bg-rose-50',
            border: 'border-rose-100'
          }].map((item, i) => <div key={i} className={`${item.bg} rounded-xl p-5 text-center border ${item.border}`} data-id="element-159">
                <div className="flex justify-center mb-3" data-id="element-160">{item.icon}</div>
                <p className="text-sm md:text-base text-gray-700 font-medium" data-id="element-161">
                  {item.text}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 10. RESULTADO */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-162">
        <div className="max-w-3xl mx-auto text-center" data-id="element-163">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-10" data-id="element-164">
            RESULTADO
          </h2>

          <div className="space-y-6 text-lg md:text-2xl text-gray-700 leading-relaxed font-medium max-w-2xl mx-auto" data-id="element-165">
            <p data-id="element-166">Entrás al consultorio sabiendo exactamente qué trabajar.</p>
            <p data-id="element-167">Las sesiones fluyen.</p>
            <p data-id="element-168">Los procesos se vuelven más claros.</p>
            <p className="pt-4" data-id="element-169">
              Y algo que muchas psicopedagogas valoran muchísimo:
            </p>
            <p className="text-teal-700 font-bold text-2xl md:text-3xl" data-id="element-170">
              terminar la jornada sin llevarse trabajo a casa.
            </p>
          </div>
        </div>
      </section>

      {/* 11. PRICING SELECTOR SECTION */}
      <section className="py-20 px-4 bg-pastel-100 relative overflow-hidden" data-id="element-171">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-172">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-pastel-300 rounded-full blur-3xl" data-id="element-173"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-pastel-200 rounded-full blur-3xl" data-id="element-174"></div>
        </div>

        <div className="max-w-2xl mx-auto relative z-10" data-id="element-175">
          <p className="text-pastel-700 text-sm font-medium mb-3 text-center uppercase tracking-wider" data-id="element-176">
            Esta oferta expira en
          </p>
          <InlineTimer data-id="element-177" />

          <div className="text-center mt-10 mb-10" data-id="element-178">
            <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900" data-id="element-179">
              Elegí tu <span className="text-pastel-700" data-id="element-180">MEGAPACK:</span>
            </h2>
            <p className="text-gray-600 mt-3 text-base md:text-lg" data-id="element-181">
              Todas las opciones incluyen los 5 bonos profesionales
            </p>
          </div>

          {/* Plan Options */}
          <div className="space-y-4 mb-8" data-id="element-182">
            {PLAN_OPTIONS.map(plan => {
            const isSelected = selectedPlan === plan.id;
            return <button key={plan.id} onClick={() => setSelectedPlan(plan.id)} className={`w-full text-left rounded-2xl p-5 md:p-6 transition-all duration-200 border-2 ${isSelected ? 'border-pastel-500 bg-white shadow-lg' : 'border-pastel-200 bg-white/60 hover:border-pastel-300 hover:bg-white/80'}`} data-id="element-183">
                  <div className="flex items-center gap-4" data-id="element-184">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-pastel-600' : 'border-pastel-300'}`} data-id="element-185">
                      {isSelected && <div className="w-3 h-3 rounded-full bg-pastel-600" data-id="element-186"></div>}
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-187">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-188">
                        <span className="text-lg" data-id="element-189">{plan.emoji}</span>
                        <h3 className="text-lg md:text-xl font-extrabold text-gray-900" data-id="element-190">
                          {plan.title}
                        </h3>
                        {plan.badge && <span className="bg-amber-400 text-amber-900 text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-191">
                            ⭐ {plan.badge}
                          </span>}
                      </div>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-192">
                        {plan.description}
                      </p>
                    </div>

                    <div className="text-right flex-shrink-0" data-id="element-193">
                      <p className="text-sm text-gray-400 line-through" data-id="element-194">
                        {plan.oldPrice}
                      </p>
                      <p className={`text-2xl md:text-3xl font-extrabold ${isSelected ? 'text-pastel-700' : 'text-gray-900'}`} data-id="element-195">
                        {plan.price}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* CTA Button */}
          <a href={currentPlan.checkoutUrl} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xl md:text-2xl font-bold py-5 px-8 rounded-2xl shadow-[0_0_40px_rgba(16,185,129,0.4)] hover:shadow-[0_0_60px_rgba(16,185,129,0.5)] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3 w-full" data-id="element-196">
            SÍ, QUIERO {currentPlan.title} →
          </a>

          <p className="text-center text-gray-500 text-sm mt-4 italic" data-id="element-197">
            Podés cambiar tu opción en cualquier momento
          </p>

          {/* What's included in selected plan */}
          <div className="mt-10 bg-white rounded-2xl p-6 md:p-8 border border-pastel-200 shadow-sm" data-id="element-198">
            <p className="text-sm font-bold text-pastel-700 uppercase tracking-wider mb-4" data-id="element-199">
              {currentPlan.title} INCLUYE:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-200">
              {currentPlan.includes.map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-201">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-202" />
                  <span className="text-sm md:text-base text-gray-700" data-id="element-203">
                    {item}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 12. GARANTÍA SECTION */}
      <section className="py-20 px-4 bg-white" data-id="element-204">
        <div className="max-w-3xl mx-auto" data-id="element-205">
          <div className="bg-emerald-50 rounded-3xl p-8 md:p-12 border-2 border-emerald-100 text-center md:text-left flex flex-col md:flex-row items-center gap-8 shadow-lg" data-id="element-206">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-md flex-shrink-0" data-id="element-207">
              <ShieldCheck className="w-12 h-12 text-emerald-500" data-id="element-208" />
            </div>
            <div data-id="element-209">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-210">
                GARANTÍA 7 DÍAS
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-2" data-id="element-211">
                Probá todo el material. Si no sentís más orden y claridad en tu
                práctica, te devolvemos el dinero.
              </p>
              <p className="text-lg font-bold text-emerald-700" data-id="element-212">
                Sin preguntas.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-12 px-4 bg-gray-50 border-t border-gray-200" data-id="element-213">
        <div className="max-w-3xl mx-auto text-center" data-id="element-214">
          <p className="text-gray-500 text-sm font-medium mb-1" data-id="element-215">
            PRODUCTO DIGITAL
          </p>
          <p className="text-gray-500 text-sm mb-1" data-id="element-216">
            Este producto es digital y se envía automáticamente después de la
            compra.
          </p>
          <p className="text-gray-500 text-sm mb-6" data-id="element-217">Acceso inmediato.</p>
          <p className="text-gray-400 text-xs" data-id="element-218">
            © 2024 MegaPack Psicopedagógico. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}